import React from 'react';
import { Card, Field, TextField, CssColorField, SaveButton, AnchorField, useSection } from '../AdminUI';
import { useSite } from '../../../context/SiteContext';
import { Switch } from '../../ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import MediaUpload from '../MediaUpload';

export default function ProductsSection() {
  const { data, update } = useSite();
  const [prodEnabled, setProdEnabled, saveProdEnabled] = useSection('productsEnabled');
  const [products, setProducts] = React.useState(data.products || []);
  // Sync from server data only on mount. Subsequent polling updates must NOT
  // clobber the admin's in-progress edits (e.g. a newly uploaded video URL).
  // After Save the update() call propagates the new state through SiteContext.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setProducts(data.products || []); }, []);

  const save = () => { update((p) => ({ ...p, products })); toast.success('Saved successfully'); };

  const updCat = (i, k, v) => setProducts(products.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addCat = () => setProducts([...products, { id: `cat_${Date.now()}`, label: 'New Category', description: '', items: [] }]);
  const remCat = (i) => setProducts(products.filter((_, idx) => idx !== i));

  const updItem = (ci, ii, k, v) => setProducts(products.map((p, idx) => idx === ci ? { ...p, items: p.items.map((it, j) => j === ii ? { ...it, [k]: v } : it) } : p));
  const addItem = (ci) => setProducts(products.map((p, idx) => idx === ci ? { ...p, items: [...p.items, { id: `it_${Date.now()}`, name: 'New Product', tagline: '', icon: 'Server', gradient: 'linear-gradient(180deg, #6b1a8b 0%, #2d0e3a 100%)', image: '', video: '', link: '', demoUrl: '', demoUser: 'demo', demoPass: 'demo123', rating: 5.0 }] } : p));
  const remItem = (ci, ii) => setProducts(products.map((p, idx) => idx === ci ? { ...p, items: p.items.filter((_, j) => j !== ii) } : p));

  return (
    <>
    <Card title="Section Visibility" desc="Turn the whole Products (B2C / B2B) section on/off on the site." footer={<SaveButton onClick={saveProdEnabled} />}>
      <div className="flex items-center gap-3" data-testid="products-enabled-row">
        <Switch checked={prodEnabled !== false} onCheckedChange={(v) => setProdEnabled(v)} data-testid="products-enabled-toggle" />
        <span className="text-sm text-zinc-400">{prodEnabled !== false ? 'Products section is visible' : 'Products section is hidden'}</span>
      </div>
    </Card>
    <Card title="Products (B2C / B2B)" desc="Manage product categories and items. Upload image + video from your PC. Videos autoplay on the site." footer={<SaveButton onClick={save} />}>
      <div className="space-y-4">
        {products.map((p, i) => (
          <div key={p.id} className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <Field label="Category ID"><TextField value={p.id} onChange={(v) => updCat(i, 'id', v)} /></Field>
              <Field label="Label"><TextField value={p.label} onChange={(v) => updCat(i, 'label', v)} /></Field>
              <Field label="Description"><TextField value={p.description} onChange={(v) => updCat(i, 'description', v)} /></Field>
            </div>
            <AnchorField anchor={`category-${p.id}`} label="Category anchor (deep-link)" />
            <div className="flex items-center justify-between mb-2 mt-3">
              <span className="text-zinc-300 text-sm font-semibold">Items ({p.items?.length || 0})</span>
              <button onClick={() => remCat(i)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"><Trash2 size={11} /> Remove Category</button>
            </div>
            <div className="space-y-2">
              {(p.items || []).map((it, j) => (
                <div key={it.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`product-item-${i}-${j}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-zinc-400 text-xs font-semibold">Item #{j + 1}</span>
                    <button onClick={() => remItem(i, j)} className="h-7 px-2 rounded-md bg-red-600/15 text-red-300 text-xs border border-red-900/40 flex items-center gap-1"><Trash2 size={11} /> Remove</button>
                  </div>
                  <AnchorField anchor={`product-${it.id}`} label="Item anchor (deep-link)" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Field label="Name"><TextField value={it.name} onChange={(v) => updItem(i, j, 'name', v)} /></Field>
                    <Field label="Tagline"><TextField value={it.tagline} onChange={(v) => updItem(i, j, 'tagline', v)} /></Field>
                    <Field label="Icon (lucide)"><TextField value={it.icon} onChange={(v) => updItem(i, j, 'icon', v)} /></Field>
                    <Field label="Pricing Kind" hint="Fixed = ₹ sale · Percent = commission %">
                      <select
                        value={it.pricingKind || 'fixed'}
                        onChange={(e) => updItem(i, j, 'pricingKind', e.target.value)}
                        className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 text-sm"
                        data-testid={`product-pricing-kind-${i}-${j}`}
                      >
                        <option value="fixed">Fixed (₹)</option>
                        <option value="percent">Percent (%)</option>
                      </select>
                    </Field>
                    <Field label="Sale Price (₹) — used when Fixed">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={it.price ?? ''}
                        onChange={(e) => updItem(i, j, 'price', e.target.value)}
                        placeholder="e.g. 1999"
                        className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 text-sm"
                        data-testid={`product-price-${i}-${j}`}
                      />
                    </Field>
                    <Field label="Percent (%) — used when Percent">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={it.percent ?? ''}
                        onChange={(e) => updItem(i, j, 'percent', e.target.value)}
                        placeholder="e.g. 5"
                        className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 text-sm"
                        data-testid={`product-percent-${i}-${j}`}
                      />
                    </Field>
                    <Field label="Visit Link"><TextField value={it.link} onChange={(v) => updItem(i, j, 'link', v)} /></Field>
                    <Field label="Rating"><TextField value={it.rating ?? ''} onChange={(v) => updItem(i, j, 'rating', v)} /></Field>
                    <Field label="Demo URL"><TextField value={it.demoUrl} onChange={(v) => updItem(i, j, 'demoUrl', v)} /></Field>
                    <Field label="Demo Username"><TextField value={it.demoUser} onChange={(v) => updItem(i, j, 'demoUser', v)} /></Field>
                    <Field label="Demo Password"><TextField value={it.demoPass} onChange={(v) => updItem(i, j, 'demoPass', v)} /></Field>
                    <Field label="Gradient (fallback)"><CssColorField value={it.gradient} onChange={(v) => updItem(i, j, 'gradient', v)} placeholder="linear-gradient(...)" /></Field>
                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                      <div>
                        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Screenshot / Image</label>
                        <MediaUpload value={it.image} onChange={(v) => updItem(i, j, 'image', v)} accept="image" previewClass="h-14 w-24" testid={`product-image-${i}-${j}`} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Desktop Video (auto-plays; 16:9)</label>
                        <MediaUpload value={it.videoDesktop || it.video} onChange={(v) => updItem(i, j, 'videoDesktop', v)} accept="media" previewClass="h-14 w-24" testid={`product-video-desktop-${i}-${j}`} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Mobile Video (portrait; 9:16)</label>
                        <MediaUpload value={it.videoMobile} onChange={(v) => updItem(i, j, 'videoMobile', v)} accept="media" previewClass="h-14 w-24" testid={`product-video-mobile-${i}-${j}`} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => addItem(i)} className="w-full h-9 rounded-md border border-dashed border-zinc-700 text-zinc-300 text-xs flex items-center justify-center gap-1"><Plus size={12} /> Add Item to {p.label}</button>
            </div>
          </div>
        ))}
        <button onClick={addCat} className="w-full h-10 rounded-md border border-dashed border-zinc-700 text-zinc-300 text-sm flex items-center justify-center gap-1.5"><Plus size={14} /> Add Category</button>
      </div>
    </Card>
    </>
  );
}
